import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Table } from "reactstrap";
import { FaArrowLeft, FaEdit, FaTrash } from "react-icons/fa";
import {
  AUTO_SCORE_MAX,
  computeAutoScores,
  CRITICAL_NEED_SCORE_THRESHOLD,
  criticalityColor,
  decisionColor,
  EVIDENCE_RATING_OPTIONS_1_TO_3,
  formatCell,
  formatDate,
  formatMoney,
  isWorkforcePlanningCompleted,
  RATING_OPTIONS_1_TO_3,
  recordStatusColor,
  recordStatusLabel,
  recordToForm,
  resolveCriticallyNeedRole,
} from "./workforcePlanningHelpers";
import "./WorkforcePlanningDetails.css";

const CRITICALITY_TIERS = [
  {
    min: 75,
    max: 100,
    label: "Mission Critical",
    description: "Highest priority — role is essential to mission delivery.",
    cssClass: "mission",
  },
  {
    min: 55,
    max: 74,
    label: "Essential",
    description: "Strong need — role significantly supports operations.",
    cssClass: "essential",
  },
  {
    min: 35,
    max: 54,
    label: "Important / Flexible",
    description: "Moderate need — role adds value but some flexibility exists.",
    cssClass: "important",
  },
  {
    min: 0,
    max: 34,
    label: "Non-Critical / Optional",
    description: "Lower priority — role can be deferred or covered elsewhere.",
    cssClass: "optional",
  },
];

const verdictCssClass = (category) => {
  const v = String(category || "").toLowerCase();
  if (v.includes("mission critical") || v === "critical") return "mission";
  if (v === "essential" || v === "high") return "essential";
  if (v.includes("important") || v === "medium") return "important";
  return "optional";
};

const Field = ({ label, value, hint, empty = "Not provided" }) => {
  const display = value !== null && value !== undefined && String(value).trim() !== "";
  return (
    <div>
      <div className="wfp-details__field-label">{label}</div>
      <div className={`wfp-details__field-value${display ? "" : " wfp-details__field-value--empty"}`}>
        {display ? value : empty}
      </div>
      {hint ? <div className="wfp-details__field-hint">{hint}</div> : null}
    </div>
  );
};

const GapValue = ({ value }) => {
  if (value === "" || value == null) return <span className="wfp-details__gap--zero">—</span>;
  const n = Number(value);
  if (Number.isNaN(n)) return formatCell(value);
  if (n < 0) return <span className="wfp-details__gap--negative">{n} (shortage)</span>;
  if (n > 0) return <span className="wfp-details__gap--positive">+{n} (surplus)</span>;
  return <span className="wfp-details__gap--zero">0 (balanced)</span>;
};

const ratingLabel = (value, options) => {
  const n = Number(value);
  if (Number.isNaN(n)) return null;
  const opt = options.find((o) => Number(o.value) === n);
  return opt ? opt.label : `${n}`;
};

const parseBudgetDetails = (record) => {
  let details = record?.budget_details;
  if (typeof details === "string") {
    try {
      details = JSON.parse(details);
    } catch {
      details = null;
    }
  }
  return Array.isArray(details) ? details : [];
};

/**
 * Shared workforce planning details body.
 * Used by the details page and the analysis modal.
 */
const WorkforcePlanningDetailsContent = ({
  record,
  showBackLink = true,
  showActions = true,
  canDelete = false,
  onDelete,
  className = "",
}) => {
  const scores = useMemo(() => {
    if (!record) return null;
    return computeAutoScores(recordToForm(record));
  }, [record]);

  const scaledScore = useMemo(() => {
    if (!scores) return 0;
    return AUTO_SCORE_MAX > 0
      ? Math.round((scores.total / AUTO_SCORE_MAX) * 100)
      : 0;
  }, [scores]);

  const criticality = record?.criticality_category || scores?.criticality_category || "";
  const activeTier = CRITICALITY_TIERS.find((t) => t.label === criticality);
  const budgetDetails = useMemo(() => parseBudgetDetails(record), [record]);
  const hasBudget =
    budgetDetails.length > 0 &&
    budgetDetails.some(
      (item) =>
        item.project_name ||
        item.budget_line ||
        (item.amount !== "" && item.amount != null) ||
        item.budget_confirmed_until
    );
  const roleNeed = record ? resolveCriticallyNeedRole(recordToForm(record)) : "";

  if (!record) {
    return (
      <div className={`wfp-details ${className}`.trim()}>
        <div className="wfp-details__section">
          <div className="wfp-details__section-body text-center text-muted py-5">
            Record not found.
          </div>
        </div>
      </div>
    );
  }

  const roleName = formatCell(record.role?.role_name || "Workforce Planning");
  const deptName = formatCell(
    record.role?.department?.department_name ||
      record.department?.department_name ||
      "—"
  );

  return (
    <div className={`wfp-details ${className}`.trim()}>
      <div className="wfp-details__hero">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div>
            {showBackLink ? (
              <Link to="/hrm/workforce-planning" className="wfp-details__back">
                <FaArrowLeft className="me-1" /> Back to list
              </Link>
            ) : null}
            <h1 className="wfp-details__hero-title">{roleName}</h1>
            <p className="wfp-details__hero-sub">
              {formatCell(record.office)} · {formatCell(record.area)} · {deptName}
            </p>
            <div className="wfp-details__hero-meta">
              <Badge color={recordStatusColor(record.status)}>
                {recordStatusLabel(record.status)}
              </Badge>
              {criticality ? (
                <Badge color={criticalityColor(criticality)}>{criticality}</Badge>
              ) : null}
              <Badge color={decisionColor(record.committee_decision)}>
                Committee: {formatCell(record.committee_decision)}
              </Badge>
              <span className="wfp-details__hero-date">
                Updated {formatDate(record.updated_at || record.created_at)}
              </span>
            </div>
          </div>
          {showActions ? (
            <div className="wfp-details__actions">
              {!isWorkforcePlanningCompleted(record.status) && (
                <Button
                  color="light"
                  size="sm"
                  tag={Link}
                  to={`/hrm/workforce-planning/edit/${record.id}`}
                >
                  <FaEdit className="me-1" /> Edit
                </Button>
              )}
              {canDelete && (
                <Button color="danger" outline size="sm" onClick={onDelete}>
                  <FaTrash className="me-1" /> Delete
                </Button>
              )}
            </div>
          ) : null}
        </div>
      </div>

      <div className="wfp-details__context-cards">
        <div className="wfp-details__context-card">
          <div className="wfp-details__context-card-label">Critically needed?</div>
          <div className="wfp-details__context-card-value">
            {roleNeed === "yes" ? "Yes" : roleNeed === "no" ? "No" : "—"}
          </div>
          <div className="wfp-details__field-hint mt-1">
            Overall score &gt; {CRITICAL_NEED_SCORE_THRESHOLD}
          </div>
        </div>
        <div className="wfp-details__context-card">
          <div className="wfp-details__context-card-label">Current headcount</div>
          <div className="wfp-details__context-card-value">
            {formatCell(record.current_staff_count)}
          </div>
        </div>
        <div className="wfp-details__context-card">
          <div className="wfp-details__context-card-label">Total score</div>
          <div className="wfp-details__context-card-value">
            {formatCell(record.total_score ?? scores?.total)} / {AUTO_SCORE_MAX}
          </div>
        </div>
      </div>

      <section className="wfp-details__section">
        <div className="wfp-details__section-header">
          <h2 className="wfp-details__section-title">Role &amp; location</h2>
          <p className="wfp-details__section-desc">
            The designation, department, and office this workforce request applies to.
          </p>
        </div>
        <div className="wfp-details__section-body">
          <div className="wfp-details__grid wfp-details__grid--3">
            <Field label="Role / designation" value={roleName} />
            <Field label="Department" value={deptName} />
            <Field label="Office" value={formatCell(record.office)} />
            <Field label="Area" value={formatCell(record.area)} hint="SC, PL, or SL region" />
            <Field
              label="Created by"
              value={
                record.creator
                  ? `${record.creator.name || "—"} (${record.creator.email || "—"})`
                  : null
              }
            />
            <Field
              label="Critically need this role?"
              value={
                roleNeed === "yes"
                  ? `Yes — overall score above ${CRITICAL_NEED_SCORE_THRESHOLD}`
                  : roleNeed === "no"
                  ? `No — overall score at or below ${CRITICAL_NEED_SCORE_THRESHOLD}`
                  : null
              }
              hint={`Determined from overall score (above ${CRITICAL_NEED_SCORE_THRESHOLD} = Yes).`}
            />
          </div>
        </div>
      </section>

      <section className="wfp-details__section">
        <div className="wfp-details__section-header">
          <h2 className="wfp-details__section-title">Staffing needs</h2>
          <p className="wfp-details__section-desc">
            Headcount and gap/surplus across portfolio stages. Gap = current staff − needed
            (negative means shortage).
          </p>
        </div>
        <div className="wfp-details__section-body">
          <Field
            label="Current staff count"
            value={formatCell(record.current_staff_count)}
            hint="Actual headcount in this office for this role today."
          />
          <div className="wfp-details__staffing-grid mt-3">
            <div className="wfp-details__stage wfp-details__stage--stress">
              <span className="wfp-details__stage-badge">Stress</span>
              <div className="wfp-details__stage-range">Portfolio &lt;8m</div>
              <div className="wfp-details__stage-row">
                <span>Minimum needed</span>
                <strong>{formatCell(record.minimum_staff_needed)}</strong>
              </div>
              <div className="wfp-details__stage-row">
                <span>Gap / surplus</span>
                <GapValue value={record.minimum_gap_surplus} />
              </div>
            </div>
            <div className="wfp-details__stage wfp-details__stage--stable">
              <span className="wfp-details__stage-badge">Stable</span>
              <div className="wfp-details__stage-range">Portfolio 8m–12m</div>
              <div className="wfp-details__stage-row">
                <span>Stable needed</span>
                <strong>{formatCell(record.stable_staff_needed)}</strong>
              </div>
              <div className="wfp-details__stage-row">
                <span>Gap / surplus</span>
                <GapValue value={record.stable_gap_surplus} />
              </div>
            </div>
            <div className="wfp-details__stage wfp-details__stage--growth">
              <span className="wfp-details__stage-badge">Growth</span>
              <div className="wfp-details__stage-range">Portfolio &gt;12m</div>
              <div className="wfp-details__stage-row">
                <span>Growth needed</span>
                <strong>{formatCell(record.growth_staff_needed)}</strong>
              </div>
              <div className="wfp-details__stage-row">
                <span>Gap / surplus</span>
                <GapValue value={record.growth_gap_surplus} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="wfp-details__section">
        <div className="wfp-details__section-header">
          <h2 className="wfp-details__section-title">Budget</h2>
          <p className="wfp-details__section-desc">
            Salary requirements and funding sources. Available budget is the sum of confirmed
            budget entries; surplus/gap compares available budget to total salary.
          </p>
        </div>
        <div className="wfp-details__section-body">
          {hasBudget ? (
            <>
              <div className="wfp-details__grid wfp-details__grid--3">
                <Field
                  label="Monthly gross salary"
                  value={formatMoney(record.monthly_gross_salary)}
                />
                <Field label="Number of months" value={formatCell(record.nbr_of_months)} />
                <Field
                  label="Total salary"
                  value={formatMoney(record.total_salary)}
                  hint="Monthly gross salary × number of months"
                />
                <Field
                  label="Available budget"
                  value={formatMoney(record.available_budget)}
                  hint="Sum of all budget entry amounts"
                />
                <Field
                  label="Budget surplus / gap"
                  value={formatMoney(record.budget_surplus_gap)}
                  hint="Available budget − total salary (positive = surplus)"
                />
              </div>
              {budgetDetails.length > 0 && (
                <div className="table-responsive mt-3">
                  <Table bordered className="wfp-details__table mb-0">
                    <thead>
                      <tr>
                        <th>Project</th>
                        <th>Budget line</th>
                        <th>Amount</th>
                        <th>Confirmed until</th>
                      </tr>
                    </thead>
                    <tbody>
                      {budgetDetails.map((item, index) => (
                        <tr key={`budget-${index}`}>
                          <td>{formatCell(item.project_name)}</td>
                          <td>{formatCell(item.budget_line)}</td>
                          <td>{formatMoney(item.amount)}</td>
                          <td>{formatDate(item.budget_confirmed_until)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          ) : (
            <Field
              label="Budget status"
              value="No budget allocated for this role"
              hint="The requester indicated there is no confirmed budget for this position."
            />
          )}
        </div>
      </section>

      <section className="wfp-details__section">
        <div className="wfp-details__section-header">
          <h2 className="wfp-details__section-title">Justification</h2>
          <p className="wfp-details__section-desc">
            Evidence and risk assessment supporting why this role is needed, and whether duties
            can be merged or shared.
          </p>
        </div>
        <div className="wfp-details__section-body">
          <div className="mb-3">
            <Field
              label="Why role is needed"
              value={formatCell(record.why_role_is_needed)}
            />
          </div>
          <div className="wfp-details__grid">
            <Field
              label="Evidence strength"
              value={ratingLabel(record.evidence_reference_score, EVIDENCE_RATING_OPTIONS_1_TO_3)}
              hint="1 = Low, 2 = Moderate, 3 = High — how strong is the supporting evidence?"
            />
            <Field
              label="Evidence reference"
              value={formatCell(record.evidence_reference)}
              hint="Explanation supporting the evidence rating above."
            />
            <Field
              label="Risk if role unavailable"
              value={ratingLabel(record.risk_if_role_is_not_available_score, RATING_OPTIONS_1_TO_3)}
              hint="1 = Low risk, 2 = Moderate, 3 = High risk to operations."
            />
            <Field
              label="Risk explanation"
              value={formatCell(record.risk_if_role_is_not_available)}
            />
            <Field
              label="Can role be merged or shared?"
              value={formatCell(record.can_this_role_be_merged_or_shared)}
            />
            <Field
              label={
                record.can_this_role_be_merged_or_shared === "Yes"
                  ? "Merged with role(s)"
                  : record.can_this_role_be_merged_or_shared === "No"
                  ? "Why it cannot be merged"
                  : "Merge / share details"
              }
              value={formatCell(record.merged_or_shared_details)}
            />
          </div>
        </div>
      </section>

      <section className="wfp-details__section">
        <div className="wfp-details__section-header">
          <h2 className="wfp-details__section-title">Recommendations</h2>
          <p className="wfp-details__section-desc">
            Head of Department requested action and any committee notes.
          </p>
        </div>
        <div className="wfp-details__section-body">
          <Field label="HoD requested action / recommendation" />
          <div
            className={`wfp-details__recommendation mt-2${
              !record.hod_requested_action_recommendation
                ? " wfp-details__recommendation--empty"
                : ""
            }`}
          >
            {formatCell(record.hod_requested_action_recommendation) ||
              "No recommendation provided."}
          </div>
          {record.comments ? (
            <div className="mt-3">
              <Field label="Committee comments" value={formatCell(record.comments)} />
            </div>
          ) : null}
        </div>
      </section>

      {scores ? (
        <section className="wfp-details__section">
          <div className="wfp-details__section-header">
            <h2 className="wfp-details__section-title">Scoring summary</h2>
            <p className="wfp-details__section-desc">
              Overall score from role need, staffing stress, budget, and justification. Scaled
              score (0–100) determines the criticality category below.
            </p>
          </div>
          <div className="wfp-details__section-body">
            <div className="wfp-details__score-summary">
              <div className="wfp-details__score-pill">
                <span className="wfp-details__score-pill-label">Role need</span>
                <span className="wfp-details__score-pill-value">
                  {formatCell(record.evidence_score ?? scores.roleNeedScore)}
                  <span className="wfp-details__score-pill-max">/ 1</span>
                </span>
              </div>
              <div className="wfp-details__score-pill">
                <span className="wfp-details__score-pill-label">Stress gap</span>
                <span className="wfp-details__score-pill-value">
                  {formatCell(record.risk_score ?? scores.stressGapScore)}
                  <span className="wfp-details__score-pill-max">/ 4</span>
                </span>
              </div>
              <div className="wfp-details__score-pill">
                <span className="wfp-details__score-pill-label">Budget</span>
                <span className="wfp-details__score-pill-value">
                  {formatCell(record.funding_compliance ?? scores.budgetScore)}
                  <span className="wfp-details__score-pill-max">/ 45</span>
                </span>
              </div>
              <div className="wfp-details__score-pill">
                <span className="wfp-details__score-pill-label">Justification</span>
                <span className="wfp-details__score-pill-value">
                  {formatCell(record.workload_score ?? scores.justificationScore)}
                  <span className="wfp-details__score-pill-max">/ 50</span>
                </span>
              </div>
            </div>
            <div className="wfp-details__score-totals">
              <div className="wfp-details__score-total-card">
                <span className="wfp-details__score-total-label">Raw total</span>
                <span className="wfp-details__score-total-value">
                  {formatCell(record.total_score ?? scores.total)}
                  <span className="wfp-details__score-total-max"> / {AUTO_SCORE_MAX}</span>
                </span>
              </div>
              <div className="wfp-details__score-total-card wfp-details__score-total-card--highlight">
                <span className="wfp-details__score-total-label">Scaled score</span>
                <span className="wfp-details__score-total-value">
                  {scaledScore}
                  <span className="wfp-details__score-total-max"> / 100</span>
                </span>
                <span className="wfp-details__score-total-hint">
                  Used for criticality category
                </span>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      <section className="wfp-details__section">
        <div className="wfp-details__section-header">
          <h2 className="wfp-details__section-title">Criticality decision</h2>
          <p className="wfp-details__section-desc">
            The criticality category is derived automatically from the scaled score (0–100).
            This indicates how urgently the organisation should act on this workforce request.
          </p>
        </div>
        <div className="wfp-details__section-body">
          <div className={`wfp-details__verdict wfp-details__verdict--${verdictCssClass(criticality)}`}>
            <div className="wfp-details__verdict-label">Criticality category</div>
            <div className="wfp-details__verdict-value">
              {criticality || "Not determined"}
            </div>
            <div className="wfp-details__verdict-score">
              Based on scaled score of <strong>{scaledScore} / 100</strong>
              {activeTier ? ` — ${activeTier.description}` : ""}
            </div>
          </div>

          <div className="wfp-details__criticality-guide">
            <div className="wfp-details__criticality-guide-title">
              How criticality is calculated
            </div>
            <div className="table-responsive">
              <Table bordered size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Scaled score range</th>
                    <th>Category</th>
                    <th>Meaning</th>
                  </tr>
                </thead>
                <tbody>
                  {CRITICALITY_TIERS.map((tier) => (
                    <tr
                      key={tier.label}
                      className={tier.label === criticality ? "is-active" : ""}
                    >
                      <td>
                        {tier.min}–{tier.max}
                      </td>
                      <td>
                        <Badge color={criticalityColor(tier.label)}>{tier.label}</Badge>
                      </td>
                      <td className="text-muted">{tier.description}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <p className="text-muted small mt-2 mb-0">
              Formula: scaled score = round(raw total ÷ {AUTO_SCORE_MAX} × 100). The category
              is assigned using the table above. Role need (Yes = 1 pt), stress gap (up to 4
              pts), budget (up to 45 pts), and justification (up to 50 pts) contribute to the
              raw total.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WorkforcePlanningDetailsContent;
